import React, { useState, useRef, useEffect } from "react";
import {
  MessageSquare,
  X,
  ThumbsUp,
  ThumbsDown,
  Copy,
  MoreVertical,
  Code,
  ChevronRight,
  Shield,
  AlertCircle,
  Send,
  Mic,
  Paperclip,
  Sparkles,
  Wrench,
  Lightbulb,
  CheckCircle2,
  Clock,
  Eye,
  ChevronDown,
} from "lucide-react";

interface ChatMessage {
  id: string;
  type: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  userEmail?: string;
  userAvatar?: string;
  toolsUsed?: number;
  showTools?: boolean;
  tools?: Array<{ name: string; description?: string }>;
  buttons?: ChatButton[];
  codePreview?: {
    title: string;
    code: string;
  };
  status?: "thinking" | "completed";
  thoughtTime?: number;
  previewText?: string;
  actionView?: string; // Type of view to show in right panel
}

interface ChatButton {
  label: string;
  type: "primary" | "secondary" | "code" | "compact";
  icon?: React.ReactNode;
  onClick?: () => void;
  actionView?: string; // Type of view to trigger in right panel
}

interface SecurityIssue {
  count: number;
  status: "error" | "warning";
  message: string;
}

interface RevOpsChatProps {
  isOpen: boolean;
  onClose: () => void;
  isClosing?: boolean;
  isOpening?: boolean;
  onActionViewChange?: (viewType: string | null) => void;
}

const RevOpsChat: React.FC<RevOpsChatProps> = ({
  isOpen,
  onClose,
  isClosing = false,
  isOpening = false,
  onActionViewChange,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      type: "user",
      content: "Help me set up the RevSync agent for the first time",
      timestamp: "Jan 18 at 7:18 PM",
      userEmail: "user@forgeworks.ai",
    },
    {
      id: "2",
      type: "assistant",
      content:
        "I'll help you set up the RevSync agent! Let me analyze your current HubSpot and Stripe connections to provide the best configuration.",
      timestamp: "Jan 18 at 7:18 PM",
      status: "thinking",
      thoughtTime: 11,
      toolsUsed: 3,
      showTools: true,
      tools: [
        { name: "Check HubSpot Connection" },
        { name: "Check Stripe Connection" },
        { name: "Analyze Pipeline Configuration" },
      ],
    },
    {
      id: "3",
      type: "assistant",
      content:
        "Great! I've analyzed your setup. Here's what I found:\n\n• HubSpot is connected ✓\n• Stripe is connected ✓\n• Your default pipeline is 'Sales Pipeline'\n• Recommended lifecycle stage: 'customer'\n\nI can help you configure:\n1. Auto-Create Stripe Subscriptions\n2. Renewal Deal Automation\n3. Customer Lifecycle Stage",
      timestamp: "Jan 18 at 7:19 PM",
      buttons: [
        {
          label: "Configure RevSync Agent",
          type: "primary",
          icon: <ChevronRight className="w-4 h-4" />,
          actionView: "settings",
        },
      ],
    },
    {
      id: "4",
      type: "user",
      content: "Let's start with the Customer Lifecycle Stage configuration",
      timestamp: "Jan 18 at 7:20 PM",
      userEmail: "user@forgeworks.ai",
    },
    {
      id: "5",
      type: "assistant",
      content:
        "Perfect! I'll configure the Customer Lifecycle Stage. Based on your HubSpot setup, I recommend using 'customer' as your lifecycle stage.",
      timestamp: "Jan 18 at 7:20 PM",
      status: "thinking",
      thoughtTime: 6,
      toolsUsed: 2,
      showTools: true,
      tools: [
        { name: "Fetch Lifecycle Stages" },
        { name: "Analyze Current Selection" },
      ],
    },
    {
      id: "6",
      type: "assistant",
      content:
        "Done! Customer Lifecycle Stage has been set to 'customer'. Companies with this lifecycle stage and at least one deal will now appear in your customer list.",
      timestamp: "Jan 18 at 7:21 PM",
      buttons: [
        {
          label: "View Settings",
          type: "secondary",
          actionView: "settings",
        },
        {
          label: "</> Code",
          type: "code",
          actionView: "code",
        },
      ],
      codePreview: {
        title: "RevSyncAgentSettings.tsx",
        code: `const handleSaveLifecycleStage = async (selectedStage: string) => {
  setIsSavingLifecycleStage(true);
  try {
    const { data } = await revSyncService.updateCustomerLifecycleStage(selectedStage);
    setCurrentLifecycleStage(data.customerLifecycleStage);
    showToast("success", data.userMessage);
  } catch (error) {
    showToast("error", "Failed to update lifecycle stage");
  }
};`,
      },
    },
  ]);

  const [inputValue, setInputValue] = useState("");
  const [securityIssue] = useState<SecurityIssue>({
    count: 1,
    status: "error",
    message: "Error (outdated)",
  });
  const [showAllTools, setShowAllTools] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleButtonClick = (button: ChatButton) => {
    if (button.actionView && onActionViewChange) {
      onActionViewChange(button.actionView);
    }
    if (button.onClick) {
      button.onClick();
    }
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: inputValue,
      timestamp: new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      userEmail: "user@forgeworks.ai",
    };

    setMessages([...messages, newMessage]);
    setInputValue("");

    // Simulate assistant response
    setTimeout(() => {
      const assistantResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        content:
          "I understand. Let me help you with that. This is a placeholder response - the actual backend integration will provide real responses.",
        timestamp: new Date().toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        status: "thinking",
        thoughtTime: 3,
        toolsUsed: 1,
        showTools: true,
      };
      setMessages((prev) => [...prev, assistantResponse]);

      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantResponse.id
              ? { ...msg, status: "completed", content: "Response completed!" }
              : msg
          )
        );
      }, 2000);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleShowAllTools = (messageId: string) => {
    setShowAllTools((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
        style={{
          opacity: isClosing ? 0 : isOpening ? 0 : 1,
          pointerEvents: isClosing ? "none" : "auto",
          transition: isClosing
            ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
        onClick={onClose}
      />

      {/* Left Side Chat Panel */}
      <div
        ref={chatRef}
        className="fixed inset-y-0 left-0 w-full max-w-md bg-background-default dark:bg-background-dark shadow-2xl z-50 flex flex-col overflow-hidden"
        style={{
          transform: isOpening
            ? "translateX(-100%)"
            : isClosing
            ? "translateX(-100%)"
            : "translateX(0)",
          opacity: isClosing ? 0 : 1,
          transition: isClosing
            ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-secondary" />
              </div>
              <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                RevOps Agent
              </span>
            </div>
            <span className="text-xs text-text-secondary dark:text-text-secondaryDark">
              Previewing last saved version
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors">
              <Clock className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            </button>
            <button className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors">
              <MoreVertical className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors"
            >
              <X className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 scrollbar-hide">
          {messages.map((message) => (
            <div key={message.id} className="space-y-3">
              {/* User Message */}
              {message.type === "user" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-secondary/20 dark:bg-secondary/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-secondary">
                        {message.userEmail?.charAt(0).toUpperCase() || "U"}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-text-primary dark:text-text-primaryDark">
                      {message.userEmail}
                    </span>
                    <span className="text-xs text-text-secondary dark:text-text-secondaryDark">
                      {message.timestamp}
                    </span>
                  </div>
                  <div className="bg-background-panel dark:bg-background-panelDark rounded-lg px-4 py-3 border border-divider dark:border-divider-dark ml-8">
                    <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark whitespace-pre-wrap">
                      {message.content}
                    </p>
                  </div>
                </div>
              )}

              {/* Assistant Message */}
              {message.type === "assistant" && (
                <div className="space-y-3">
                  {/* Thought indicator */}
                  {message.status === "thinking" && (
                    <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                      <Lightbulb className="w-4 h-4" />
                      <span>Thought for {message.thoughtTime}s</span>
                    </div>
                  )}

                  {/* Tools used indicator */}
                  {message.showTools && message.toolsUsed && (
                    <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                      <Wrench className="w-4 h-4" />
                      <span>
                        {message.toolsUsed} tool{message.toolsUsed > 1 ? "s" : ""} used
                      </span>
                      {message.tools && message.tools.length > 0 && (
                        <button
                          onClick={() => toggleShowAllTools(message.id)}
                          className="text-xs text-secondary hover:underline ml-auto flex items-center gap-1"
                        >
                          {showAllTools[message.id] ? "Hide" : "Show all"}
                          {showAllTools[message.id] ? (
                            <ChevronDown className="w-3 h-3 rotate-180" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Expanded tools list */}
                  {message.tools &&
                    message.tools.length > 0 &&
                    showAllTools[message.id] && (
                      <div className="ml-6 space-y-1.5 pl-4 border-l-2 border-divider dark:border-divider-dark">
                        {message.tools.map((tool, idx) => (
                          <div
                            key={idx}
                            className="text-xs text-text-secondary dark:text-text-secondaryDark"
                          >
                            <span className="font-medium">{tool.name}</span>
                            {tool.description && (
                              <span className="ml-2">- {tool.description}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Message content */}
                  <div className="bg-background-panel dark:bg-background-panelDark rounded-lg px-4 py-3 border border-divider dark:border-divider-dark">
                    <p className="text-sm leading-relaxed text-text-primary dark:text-text-primaryDark whitespace-pre-wrap">
                      {message.content}
                    </p>
                  </div>

                  {/* Code preview */}
                  {message.codePreview && (
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border-b border-divider dark:border-divider-dark">
                        <div className="flex items-center gap-2">
                          <Code className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
                          <span className="text-xs font-medium text-text-primary dark:text-text-primaryDark">
                            {message.codePreview.title}
                          </span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(message.codePreview!.code)}
                          className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                          title="Copy code"
                        >
                          <Copy className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
                        </button>
                      </div>
                      <div className="px-4 py-3">
                        <pre className="text-xs text-text-primary dark:text-text-primaryDark font-mono overflow-x-auto">
                          <code>{message.codePreview.code}</code>
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  {message.buttons && message.buttons.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {message.buttons.map((button, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleButtonClick(button)}
                          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            button.type === "primary"
                              ? "bg-secondary hover:bg-secondary-hover text-white shadow-sm hover:shadow-md"
                              : button.type === "code"
                              ? "bg-background-default dark:bg-background-dark hover:bg-background-panel dark:hover:bg-background-panelDark border border-divider dark:border-divider-dark text-text-primary dark:text-text-primaryDark"
                              : button.type === "compact"
                              ? "px-3 py-1.5 text-xs bg-background-default dark:bg-background-dark hover:bg-background-panel dark:hover:bg-background-panelDark border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark"
                              : "bg-background-default dark:bg-background-dark hover:bg-background-panel dark:hover:bg-background-panelDark border border-divider dark:border-divider-dark text-text-primary dark:text-text-primaryDark"
                          }`}
                        >
                          {button.icon}
                          {button.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Preview text */}
                  {message.previewText && (
                    <div className="text-xs text-text-secondary dark:text-text-secondaryDark italic">
                      {message.previewText}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                      title="Thumbs up"
                    >
                      <ThumbsUp className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                    </button>
                    <button
                      className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                      title="Thumbs down"
                    >
                      <ThumbsDown className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                    </button>
                    <button
                      onClick={() => copyToClipboard(message.content)}
                      className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                      title="Copy message"
                    >
                      <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                    </button>
                    <button
                      className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Security Section */}
        <div className="px-6 py-2.5 border-t border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
              Security
            </span>
            <span className="text-xs text-red-600 dark:text-red-400">
              {securityIssue.count} {securityIssue.message}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-sm font-medium bg-secondary hover:bg-secondary-hover text-white rounded-lg transition-colors">
              Review
            </button>
            <button className="p-1 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors">
              <X className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
            </button>
          </div>
        </div>

        {/* Input Area */}
        <div className="px-6 py-3 border-t border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors"
              title="Attach file"
            >
              <Paperclip className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask RevOps Agent..."
              className="flex-1 px-4 py-2 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-colors"
            />
            <button
              className="px-3 py-1.5 text-xs font-medium bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors flex items-center gap-1.5"
              title="Visual edits"
            >
              <Eye className="w-3.5 h-3.5" />
              Visual edits
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="p-2 bg-secondary hover:bg-secondary-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
              <button
                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                title="Voice input"
              >
                <Mic className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevOpsChat;
